import {
  SocialIcon,
  type SocialIconFill,
  type SocialIconShape,
  type SocialNetwork,
} from "@keepoinc/site-page-react-component";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Theme } from "../../../../../../model/theme";
import { classNames } from "../../../../../../utils/styles";
import { KindOptions, KindsContainer } from "../BackgroundKinds";

/* A sample on the page's color, as the Keepo shows it */
const Stage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1rem;
  border-radius: 12px;
`;

/* A card of the catalog (or prices, highlights): its photo on top, its texts in the cards' colors */
const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: min(100%, 220px);
  overflow: hidden;
  font-family: Poppins, sans-serif;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  .photo {
    height: 96px;
    background-color: rgba(0, 0, 0, 0.08);
    background-position: center;
    background-size: cover;
  }

  .texts {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.75rem;
  }

  strong {
    font-size: 0.875rem;
    font-weight: 600;
  }

  .sub {
    font-size: 0.6875rem;
    line-height: 1rem;
  }

  .link {
    align-self: flex-start;
    margin-top: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    border-bottom: 1px solid currentColor;
  }
`;

/** Cartões: a card as the Keepo's catalog shows it, in the cards' colors (the buttons' style isn't theirs). */
export const CardPreview = ({ theme, photo }: { theme: Theme; photo?: string }) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.settings.groups.previews";
  return (
    <Stage style={{ backgroundColor: theme.primary }} aria-hidden="true">
      <Card style={{ backgroundColor: theme.cardColor, color: theme.cardText }}>
        <span className="photo" style={photo ? { backgroundImage: `url("${photo.replace(/"/g, "%22")}")` } : {}} />
        <div className="texts">
          <strong>{t(`${prefix}.cardTitle`)}</strong>
          <span className="sub">{t(`${prefix}.cardText`)}</span>
          <span className="link">{t(`${prefix}.cardLink`)}</span>
        </div>
      </Card>
    </Stage>
  );
};

/** The networks shown in the icons' sample. */
const NETWORKS: SocialNetwork[] = ["instagram", "whatsapp", "tiktok", "youtube"];

/** How the social icons look: a circle or a rounded square, filled or only its outline. */
export type IconLook = { shape: SocialIconShape; fill: SocialIconFill };

const SHAPES: SocialIconShape[] = ["ROUND", "SQUARE"];
const FILLS: SocialIconFill[] = ["FILLED", "OUTLINE"];

/** Ícones: the social icons in the Keepo's colors and look, as its Redes sociais section draws them. */
export const IconsPreview = ({ theme, look }: { theme: Theme; look?: IconLook }) => (
  <Stage style={{ backgroundColor: theme.primary }} aria-hidden="true">
    {NETWORKS.map(network => (
      <SocialIcon
        key={network}
        network={network}
        shape={look?.shape}
        fill={look?.fill}
        // as the Redes sociais section: `color` is the shape's (the icons' background), `background` the symbol's
        color={theme.iconsBackground}
        background={theme.iconsColor}
        size={36}
      />
    ))}
  </Stage>
);

/* Formato and Preenchimento side by side, half a row each: three sixths each (with the gap of the cards between
   them), so their cards line up with every other row's */
const LookPair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;

  ${KindOptions} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

/** One row of cards: each a look (the row's choice over the other one already picked) drawn in the Keepo's colors. */
const LookRow = ({
  theme,
  label,
  looks,
  selected,
  name,
  onChange,
}: {
  theme: Theme;
  label: string;
  looks: IconLook[];
  selected: (look: IconLook) => boolean;
  name: (look: IconLook) => string;
  onChange: (look: IconLook) => void;
}) => (
  <KindsContainer>
    <span className="label" aria-hidden="true">
      {label}
    </span>
    <KindOptions role="radiogroup" aria-label={label}>
      {looks.map(look => (
        <button
          key={`${look.shape}-${look.fill}`}
          type="button"
          role="radio"
          aria-checked={selected(look)}
          className={classNames({ selected: selected(look) })}
          onClick={() => onChange(look)}>
          <span className="frame" aria-hidden="true">
            <span className="thumbnail" style={{ backgroundColor: theme.primary, gap: "3px" }}>
              {NETWORKS.slice(0, 3).map(network => (
                <SocialIcon
                  key={network}
                  network={network}
                  shape={look.shape}
                  fill={look.fill}
                  color={theme.iconsBackground}
                  background={theme.iconsColor}
                  size={18}
                />
              ))}
            </span>
          </span>
          {name(look)}
        </button>
      ))}
    </KindOptions>
  </KindsContainer>
);

/**
 * Ícones › the look of the Redes sociais section's icons (kept on it): its shape (Redondo / Quadrado) and its fill
 * (Preenchido / Vazado), side by side, each a half row of cards drawn in the Keepo's colors.
 */
export const IconLooks = ({
  theme,
  value,
  onChange,
}: {
  theme: Theme;
  value: IconLook;
  onChange: (look: IconLook) => void;
}) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.social.form.iconLook";
  return (
    <LookPair>
      <LookRow
        theme={theme}
        label={t(`${prefix}.shape`)}
        looks={SHAPES.map(shape => ({ ...value, shape }))}
        selected={look => look.shape === value.shape}
        name={look => t(`${prefix}.shapes.${look.shape}`)}
        onChange={onChange}
      />
      <LookRow
        theme={theme}
        label={t(`${prefix}.fill`)}
        looks={FILLS.map(fill => ({ ...value, fill }))}
        selected={look => look.fill === value.fill}
        name={look => t(`${prefix}.fills.${look.fill}`)}
        onChange={onChange}
      />
    </LookPair>
  );
};
